import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useFormContext, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Add, Delete } from '@mui/icons-material'
import {
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Button, Card, CardTitle, Slider } from 'components'
import { useBinsService, useBlendingService } from 'services'
import { successToast } from 'utils/toastService'
import {
  BlendMixerActions,
  BlendMixerButton,
  BlendMixerIconContainer,
  BlendMixerItem,
  BlendMixerItemSection,
  BlendMixerTotalCostArea,
  CostText,
  MainAreaWrapper,
  MixerToolsButton,
  MixerToolsContainer,
  SliderContainer,
  SliderValue,
  StyledIconButton,
  TogglePinAllIconContainer,
  TogglePinButtonWrapper,
} from './BlendMixer.styled'
import { BlendSelectableCard } from '../BlendSelectableCard/BlendSelectableCard'
import { BlendAttributeItem } from '../BlendAttributeItem/BlendAttributeItem'
import Dropdown from '../../../../../../components/ui/Dropdown/Dropdown'
import {
  TAttribute,
  TBins,
  TBlend,
  TCalculateMixer,
} from '../../../../../../types'
import { Attributes } from '../../../../Blends.styled'
import PinIcon from '../../../../../../assets/svgComponents/Pin'
import UnpinIcon from '../../../../../../assets/svgComponents/Unpin'
import { isNumberOutOfRange } from '../../../../../../utils/helpers'
import { useAlertModal } from '../../../../../../components/dialogs'

export const BlendMixer = ({
  setFieldsChanged,
}: {
  setFieldsChanged: Dispatch<SetStateAction<boolean>>
}) => {
  const { modal, showWarningModal } = useAlertModal()

  const { getBins } = useBinsService()
  const { calculateBlendMixer } = useBlendingService()
  const { data: bins } = useQuery({
    queryKey: ['bins-list'],
    queryFn: () => getBins(),
  })

  const { watch, control, setValue, getValues } = useFormContext<TBlend>()
  const mixers = watch('mixers', [])
  const suggestions = watch('suggestions', [])

  const totalCost =
    suggestions.find((suggestion) => suggestion.selected)?.cost ||
    mixers.find((mixer) => mixer.selected)?.cost ||
    0

  const [lockedBins, setLockedBins] = useState<{ [key: string]: boolean }>(() =>
    mixers.reduce(
      (acc, mixer, mIndex) => {
        mixer.mixer_bins.forEach((_, bIndex) => {
          acc[`${mIndex}-${bIndex}`] = false
        })
        return acc
      },
      {} as { [key: string]: boolean }
    )
  )

  const isAllLocked = useMemo(() => {
    const lockStates = Object.values(lockedBins)
    if (lockStates.length === 0) return false
    return lockStates.every((isLocked) => isLocked === true)
  }, [lockedBins])

  const handleToggleAllLock = () => {
    const newLockedState = !isAllLocked
    const newLockedBins = { ...lockedBins }

    Object.keys(newLockedBins).forEach((key) => {
      newLockedBins[key] = newLockedState
    })

    setLockedBins(newLockedBins)
  }

  const binOptions =
    bins?.results
      ?.filter((bin) => Number(bin.code) >= 0)
      .map((bin) => ({
        value: bin.id?.toString(),
        label: bin.name,
      })) || []

  const handleSelectMixer = (index: number) => {
    setValue(
      'suggestions',
      suggestions.map((suggestion) => ({ ...suggestion, selected: false }))
    )

    const updatedMixers = mixers.map((mixer, i) => ({
      ...mixer,
      selected: i === index,
    }))

    setValue('mixers', updatedMixers)
  }

  const handleBinSelection = (
    mixerIndex: number,
    binIndex: number,
    newBinId: number
  ) => {
    const selectedBin = bins?.results?.find(
      (bin) => bin.id === Number(newBinId)
    )

    if (!selectedBin) return

    const updatedMixers = mixers.map((mixer, mIndex) =>
      mIndex === mixerIndex
        ? {
            ...mixer,
            mixer_bins: mixer.mixer_bins.map((bin, bIndex) =>
              bIndex === binIndex
                ? {
                    ...bin,
                    bin_id: selectedBin.id,
                    bin_name: selectedBin.name,
                    bin_color: selectedBin.color?.hex_code || '#000',
                  }
                : bin
            ),
          }
        : mixer
    )

    setValue('mixers', updatedMixers)
  }

  const STEP = 2.5
  const MAX = 100
  const MIN = STEP

  const clampToStep = (value: number) =>
    Math.max(MIN, Math.round(value / STEP) * STEP)

  const handleBinValueChangeLocked = (
    mixerIndex: number,
    binIndex: number,
    rawValue: number
  ) => {
    const updatedMixers = [...mixers]
    const mixer = updatedMixers[mixerIndex]
    if (!mixer) return

    const bins = [...mixer.mixer_bins]

    const otherTotal = bins.reduce(
      (sum, bin, i) => (i === binIndex ? sum : sum + bin.value),
      0
    )

    const maxAllowed = Math.max(MIN, MAX - otherTotal)

    const clamped = clampToStep(rawValue)
    const newValue = Math.min(clamped, maxAllowed)

    bins[binIndex] = { ...bins[binIndex], value: newValue }

    updatedMixers[mixerIndex] = { ...mixer, mixer_bins: bins }
    setValue('mixers', updatedMixers)
  }

  const handleBinValueChange = (
    mixerIndex: number,
    binIndex: number,
    rawValue: number
  ) => {
    const updatedMixers = [...mixers]
    const mixer = updatedMixers[mixerIndex]
    if (!mixer) return

    const bins = [...mixer.mixer_bins]
    const isManual = lockedBins[`${mixerIndex}-${binIndex}`] // bin is manually controlled

    // Manual bins (including current) to exclude from auto redistribution
    const manualIndices = bins
      .map((_, i) => i)
      .filter((i) => i === binIndex || lockedBins[`${mixerIndex}-${i}`])

    const adjustableIndices = bins
      .map((_, i) => i)
      .filter((i) => !manualIndices.includes(i))

    // Total of other manual values (excluding current bin)
    const otherManualTotal = manualIndices
      .filter((i) => i !== binIndex)
      .reduce((sum, i) => sum + bins[i].value, 0)

    // Minimum space that must be reserved for others
    const minRequiredForOthers = adjustableIndices.length * MIN

    // Final cap
    const maxAllowed = isManual
      ? MAX - otherManualTotal
      : MAX - otherManualTotal - minRequiredForOthers

    const rawClamped = clampToStep(rawValue)
    const newValue = Math.min(rawClamped, maxAllowed)

    bins[binIndex] = { ...bins[binIndex], value: newValue }

    const fixedTotal = manualIndices.reduce((sum, i) => sum + bins[i].value, 0)
    const spaceLeft = MAX - fixedTotal

    if (adjustableIndices.length === 0) {
      updatedMixers[mixerIndex] = { ...mixer, mixer_bins: bins }
      setValue('mixers', updatedMixers)
      return
    }

    const currentTotal = adjustableIndices.reduce(
      (sum, i) => sum + mixer.mixer_bins[i].value,
      0
    )

    let newValues: Record<number, number> = {}

    if (currentTotal <= 0) {
      const even = clampToStep(spaceLeft / adjustableIndices.length)
      let leftover = spaceLeft - even * adjustableIndices.length
      adjustableIndices.forEach((i) => (newValues[i] = even))
      for (let i = 0; leftover >= STEP && i < adjustableIndices.length; i++) {
        newValues[adjustableIndices[i]] += STEP
        leftover -= STEP
      }
    } else {
      const proportions = adjustableIndices.map((i) => ({
        i,
        ratio: mixer.mixer_bins[i].value / currentTotal,
      }))

      let totalAssigned = 0
      proportions.forEach(({ i, ratio }) => {
        const target = clampToStep(spaceLeft * ratio)
        newValues[i] = target
        totalAssigned += target
      })

      let diff = spaceLeft - totalAssigned
      const sorted = proportions
        .map((p) => p.i)
        .sort((a, b) =>
          diff > 0 ? newValues[b] - newValues[a] : newValues[a] - newValues[b]
        )

      for (let i of sorted) {
        if (Math.abs(diff) < STEP) break
        const next = newValues[i] + Math.sign(diff) * STEP
        if (next >= MIN) {
          newValues[i] = next
          diff -= Math.sign(diff) * STEP
        }
      }
    }

    adjustableIndices.forEach((i) => {
      bins[i] = { ...bins[i], value: newValues[i] }
    })

    const finalTotal = bins.reduce((sum, bin) => sum + bin.value, 0)
    const finalDiff = MAX - finalTotal

    if (Math.abs(finalDiff) >= STEP) {
      const fallback =
        adjustableIndices.length > 0 ? adjustableIndices : [binIndex]
      fallback.sort((a, b) =>
        finalDiff > 0
          ? bins[b].value - bins[a].value
          : bins[a].value - bins[b].value
      )

      for (let i of fallback) {
        const bump = bins[i].value + Math.sign(finalDiff) * STEP
        if (bump >= MIN) {
          bins[i] = { ...bins[i], value: bump }
          break
        }
      }
    }

    updatedMixers[mixerIndex] = { ...mixer, mixer_bins: bins }
    setValue('mixers', updatedMixers)
  }

  const handleDeleteMixerRow = (mixerIndex: number, binIndex: number) => {
    const updatedMixers = mixers.map((mixer, i) => {
      if (i !== mixerIndex) return mixer

      return {
        ...mixer,
        mixer_bins: mixer.mixer_bins.filter((_, bIndex) => bIndex !== binIndex),
      }
    })

    setValue('mixers', updatedMixers)

    // Update lockedBins state to remove the deleted bin and reindex remaining bins
    const newLockedBins = { ...lockedBins }
    // Remove the deleted bin
    delete newLockedBins[`${mixerIndex}-${binIndex}`]

    Object.keys(newLockedBins).forEach((key) => {
      const [mIdx, bIdx] = key.split('-').map(Number)
      if (mIdx === mixerIndex && bIdx > binIndex) {
        const newKey = `${mIdx}-${bIdx - 1}`
        newLockedBins[newKey] = newLockedBins[key]
        delete newLockedBins[key]
      }
    })

    setLockedBins(newLockedBins)
  }

  const handleAddMixerRow = () => {
    const newBin = {
      bin_id: undefined,
      value: 0,
      bin_name: '',
      bin_color: '#479CFF',
    }

    const updatedMixers = [
      {
        ...mixers?.[0],
        mixer_bins: [...mixers?.[0].mixer_bins, newBin],
      },
    ]

    setValue('mixers', updatedMixers)

    const newBinIndex = mixers[0].mixer_bins.length
    setLockedBins((prev) => ({
      ...prev,
      [`0-${newBinIndex}`]: isAllLocked,
    }))
  }

  const handleLockToggle = (mixerIndex: number, binIndex: number) => {
    setLockedBins((prev) => ({
      ...prev,
      [`${mixerIndex}-${binIndex}`]: !prev[`${mixerIndex}-${binIndex}`],
    }))
  }

  const {
    mutate: calculateBlendMixerMutation,
    isLoading: calculateBlendMixerMutationLoading,
  } = useMutation(
    ({
      mixerBins,
      productAttributes,
    }: {
      mixerBins: TBins[]
      productAttributes: TAttribute[]
    }) => calculateBlendMixer(mixerBins, productAttributes),
    {
      onSuccess: (data) => {
        if (!data?.result) return
        successToast('Mixer changes applied')
        // TODO: move to generic type
        if (data.result.status === 'warning') {
          toast.warning(data.result.detail)
        }

        if (mixers.length > 0) {
          const updatedMixer: TCalculateMixer = {
            ...mixers[0],
            mixer_attributes: data.result.mixer_attributes,
            cost: data.result.mixer_cost,
          }
          setValue('mixers', [updatedMixer])
        }
      },
      onError: (error: { detail?: string }) => {
        toast.error(error.detail || 'Error while applying mixer changes')
      },
    }
  )

  const [mixerSumValueError, setMixerSumValueError] = useState<string | null>(
    null
  )

  const handleCalculateMixerCost = () => {
    const { attributes, mixers } = getValues()
    setMixerSumValueError(null)
    setFieldsChanged(false)
    const mixerAttributes = mixers?.[0]?.mixer_attributes ?? []
    const outOfRange = mixerAttributes.some((attr) =>
      isNumberOutOfRange(
        Number(attr.range_min),
        Number(attr.range_max),
        attr.value
      )
    )

    if (outOfRange) {
      showWarningModal({
        children:
          'One or more mixer attribute values are outside the allowed range. Please review the values before proceeding.',
      })
    }

    if (mixers && mixers.length > 0 && mixers[0].mixer_bins) {
      const missingBinId = mixers[0].mixer_bins.some((bin) => !bin.bin_id)

      if (missingBinId) {
        setMixerSumValueError('All bins must have a bin selected')
        return
      }

      const totalSum = mixers[0].mixer_bins.reduce(
        (sum, bin) => sum + (bin.value || 0),
        0
      )

      const roundedSum = Math.round(totalSum * 10) / 10

      if (roundedSum !== 100) {
        setMixerSumValueError('A sum of mixer allocation should be 100%')
        return
      }

      calculateBlendMixerMutation({
        mixerBins: mixers[0].mixer_bins,
        productAttributes: attributes,
      })
    }
  }

  return (
    <Card
      p="16px"
      flex={1}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <CardTitle title="Blend Mixer">
        <Button onClick={handleAddMixerRow}>
          <Add />
        </Button>
      </CardTitle>
      {mixers.map((mixer, mixerIndex) => (
        <BlendSelectableCard
          key={mixer.id || mixerIndex}
          selected={mixer.selected}
          onClick={() => {
            handleSelectMixer(mixerIndex)
            setFieldsChanged(true)
          }}
          footer={
            <Stack width="100%">
              <BlendMixerActions>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Button
                    sx={BlendMixerButton}
                    size="small"
                    onClick={handleCalculateMixerCost}
                    disabled={
                      calculateBlendMixerMutationLoading || !!mixerSumValueError
                    }
                  >
                    {calculateBlendMixerMutationLoading ? (
                      <CircularProgress size={24} color="primary" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                  <Typography fontSize="13px" color="error">
                    {mixerSumValueError}
                  </Typography>
                </Stack>
                <CostText>Cost: {mixer.cost} $/T</CostText>
              </BlendMixerActions>
            </Stack>
          }
        >
          <MainAreaWrapper>
            <MixerToolsContainer>
              <MixerToolsButton>
                <Tooltip
                  title={
                    <div>
                      <strong>{isAllLocked ? 'Unpin All' : 'Pin All'}</strong>
                      <hr style={{ margin: '4px 0' }} />
                      {isAllLocked ? (
                        <ul style={{ margin: '0', paddingLeft: '16px' }}>
                          <li>
                            Sliders will automatically adjust when you change
                            other values
                          </li>
                          <li>Total always maintains 100%</li>
                          <li>
                            <strong>Tip:</strong> Pin a slider after setting its
                            desired value to keep it fixed while adjusting
                            others
                          </li>
                        </ul>
                      ) : (
                        <ul style={{ margin: '0', paddingLeft: '16px' }}>
                          <li>Locks all slider positions</li>
                          <li>
                            Prevents automatic adjustments when changing other
                            values
                          </li>
                          <li>You can still manually adjust each slider</li>
                        </ul>
                      )}
                    </div>
                  }
                >
                  <IconButton onClick={handleToggleAllLock}>
                    {isAllLocked ? (
                      <TogglePinAllIconContainer>
                        <UnpinIcon />
                      </TogglePinAllIconContainer>
                    ) : (
                      <TogglePinAllIconContainer>
                        <PinIcon />
                      </TogglePinAllIconContainer>
                    )}
                  </IconButton>
                </Tooltip>
              </MixerToolsButton>
            </MixerToolsContainer>
            {mixer.mixer_bins.map((bin, binIndex) => (
              <BlendMixerItem
                key={`${mixerIndex}-${binIndex}-${bin.bin_id || 'new'}`}
                sx={{ opacity: bin.value === 0 ? 0.5 : 1 }}
              >
                <BlendMixerItemSection flex={2}>
                  <Controller
                    name={`mixers.${mixerIndex}.mixer_bins.${binIndex}.bin_id`}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        label="Bins"
                        options={binOptions}
                        value={bin.bin_id?.toString()}
                        size="small"
                        onChange={(e) => {
                          setMixerSumValueError(null)
                          handleBinSelection(
                            mixerIndex,
                            binIndex,
                            e.target.value
                          )
                          setFieldsChanged(true)
                        }}
                        ref={field.ref}
                      />
                    )}
                  />
                </BlendMixerItemSection>
                <BlendMixerItemSection flex={3}>
                  <SliderContainer>
                    <Controller
                      name={`mixers.${mixerIndex}.mixer_bins.${binIndex}.value`}
                      control={control}
                      defaultValue={bin.value}
                      render={({ field }) => (
                        <Slider
                          {...field}
                          size="medium"
                          min={0}
                          max={100}
                          step={2.5}
                          minAllowedValue={2.5}
                          customColor={bin.bin_color}
                          hideRangeValues={true}
                          onChange={(_, newValue) => {
                            setMixerSumValueError(null)
                            handleBinValueChange(
                              mixerIndex,
                              binIndex,
                              parseFloat(newValue)
                            )
                            setFieldsChanged(true)
                          }}
                        />
                      )}
                    />
                    <SliderValue>{bin.value.toFixed(1)}%</SliderValue>
                  </SliderContainer>
                </BlendMixerItemSection>
                <BlendMixerIconContainer>
                  <StyledIconButton
                    onClick={() => handleLockToggle(mixerIndex, binIndex)}
                  >
                    {lockedBins[`${mixerIndex}-${binIndex}`] ? (
                      <TogglePinButtonWrapper>
                        <PinIcon />
                      </TogglePinButtonWrapper>
                    ) : (
                      <TogglePinButtonWrapper>
                        <UnpinIcon />
                      </TogglePinButtonWrapper>
                    )}
                  </StyledIconButton>
                </BlendMixerIconContainer>
                <BlendMixerIconContainer>
                  <StyledIconButton
                    onClick={() => handleDeleteMixerRow(mixerIndex, binIndex)}
                  >
                    <Delete fontSize="inherit" />
                  </StyledIconButton>
                </BlendMixerIconContainer>
              </BlendMixerItem>
            ))}
          </MainAreaWrapper>
          <Stack
            sx={{
              padding: 2,
            }}
          >
            <Attributes
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              {mixer.mixer_attributes
                ?.filter((attr) => Number(attr.range_max) > 0)
                ?.map((attr, index) => (
                  <BlendAttributeItem
                    key={attr.attribute_id || index}
                    attribute={attr}
                  />
                ))}
            </Attributes>
          </Stack>
        </BlendSelectableCard>
      ))}
      <BlendMixerTotalCostArea>
        <CostText>Selected Blend Cost: {totalCost} $/T</CostText>
      </BlendMixerTotalCostArea>
      {modal}
    </Card>
  )
}
